/**
 * The unread counts on the home page's app tiles (I-3769): what the settings
 * ask for and how a count reads on a tile. The counts themselves come from the
 * apps' backends, chat's `GET /v1/unread` and mail's `GET /v1/mail/unread`.
 * The shell asks them directly and loads no app bundle for it.
 *
 * The settings are fields of the shell's key, `shell` → `home.unread.*`,
 * declared in `settingsHub/connectSettingsSections.ts`.
 */
import { notificationMuteActive, type NotificationMuteValue } from '@blpde/connect/config';

/** The defaults, for the declaration and the reader. */
export const HOME_UNREAD_DEFAULTS = {
  enabled: true,
  chat: true,
  mail: true,
  display: 'number',
  limit: 99,
  refresh_seconds: 60,
  chat_muted: true,
  chat_direct: true,
  chat_channels: true,
  mail_folders: 'inbox',
  mail_junk: false,
  mail_trash: false,
} as const;

export type HomeUnreadDisplay = 'number' | 'dot';
export type HomeUnreadMailFolders = 'inbox' | 'all';

/** The stored document under `home.unread`; every field optional. */
export interface HomeUnreadDocument {
  enabled?: boolean;
  chat?: boolean;
  mail?: boolean;
  display?: string;
  limit?: number;
  refresh_seconds?: number;
  chat_muted?: boolean;
  chat_direct?: boolean;
  chat_channels?: boolean;
  mail_folders?: string;
  mail_junk?: boolean;
  mail_trash?: boolean;
  /** Per shared or own account: `false` leaves it out of the mail count. */
  mail_accounts?: Record<string, boolean>;
}

/** The part of the `shell` document the counts read. */
export interface HomeUnreadShellDocument {
  home?: { unread?: HomeUnreadDocument };
  notifications?: { mutes?: { chat?: { channel?: Record<string, NotificationMuteValue> } } };
}

export interface HomeUnreadSettings {
  enabled: boolean;
  chat: boolean;
  mail: boolean;
  display: HomeUnreadDisplay;
  limit: number;
  refreshSeconds: number;
  chatMuted: boolean;
  chatDirect: boolean;
  chatChannels: boolean;
  mailFolders: HomeUnreadMailFolders;
  mailJunk: boolean;
  mailTrash: boolean;
  excludedMailAccounts: string[];
}

function positive(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
}

export function homeUnreadSettings(doc: HomeUnreadShellDocument | null | undefined): HomeUnreadSettings {
  const stored = doc?.home?.unread ?? {};
  const defaults = HOME_UNREAD_DEFAULTS;
  return {
    enabled: stored.enabled ?? defaults.enabled,
    chat: stored.chat ?? defaults.chat,
    mail: stored.mail ?? defaults.mail,
    display: stored.display === 'dot' ? 'dot' : defaults.display,
    limit: positive(stored.limit, defaults.limit),
    refreshSeconds: positive(stored.refresh_seconds, defaults.refresh_seconds),
    chatMuted: stored.chat_muted ?? defaults.chat_muted,
    chatDirect: stored.chat_direct ?? defaults.chat_direct,
    chatChannels: stored.chat_channels ?? defaults.chat_channels,
    mailFolders: stored.mail_folders === 'all' ? 'all' : defaults.mail_folders,
    mailJunk: stored.mail_junk ?? defaults.mail_junk,
    mailTrash: stored.mail_trash ?? defaults.mail_trash,
    excludedMailAccounts: Object.entries(stored.mail_accounts ?? {})
      .filter(([, counted]) => counted === false)
      .map(([id]) => id)
      .sort(),
  };
}

/** The channels muted right now (`shell` → `notifications.mutes.chat.channel`). */
export function mutedChatChannels(
  doc: HomeUnreadShellDocument | null | undefined,
  now: number = Date.now()
): string[] {
  return Object.entries(doc?.notifications?.mutes?.chat?.channel ?? {})
    .filter(([, value]) => notificationMuteActive(value, now))
    .map(([id]) => id)
    .sort();
}

/** The query of chat's count; null when no channel type is left to count. */
export function chatUnreadQuery(settings: HomeUnreadSettings, muted: readonly string[]): string | null {
  if (!settings.chatDirect && !settings.chatChannels) return null;
  const params = new URLSearchParams();
  if (!settings.chatDirect) params.set('types', 'group,public');
  if (!settings.chatChannels) params.set('types', 'direct');
  if (!settings.chatMuted && muted.length) params.set('excludeChannels', muted.join(','));
  return params.toString();
}

/** The query of mail's count. */
export function mailUnreadQuery(settings: HomeUnreadSettings): string {
  const params = new URLSearchParams({ folders: settings.mailFolders });
  if (settings.mailJunk) params.set('junk', 'true');
  if (settings.mailTrash) params.set('trash', 'true');
  if (settings.excludedMailAccounts.length) {
    params.set('excludeAccounts', settings.excludedMailAccounts.join(','));
  }
  return params.toString();
}

/** The sticker's text: the count, or `<limit>+` above the limit. */
export function homeUnreadBadge(count: number, limit: number): number | string {
  return count > limit ? `${limit}+` : count;
}
